/**
 * Shared bits for the admin infrastructure screens (agent keys, network
 * rules). Small on purpose — one refusal state and one error-to-
 * sentence helper; anything bigger belongs in `components/ui`.
 */

import { ShieldCheck } from 'lucide-react';
import { EmptyState } from '../../components/ui';
import { ApiRequestError, ApiTransportError } from '../../lib/api';

/**
 * The client-side courtesy for a user who reached an admin URL without
 * `config.manage` here. The API is the control (it answers 403 either way);
 * this buys an honest sentence instead of a screen that renders and fills
 * with refusals — the `RequireCountry` doctrine, one level down.
 */
export function NoAdminAccess(): React.JSX.Element {
  return (
    <div className="px-6 py-10">
      <EmptyState
        icon={<ShieldCheck aria-hidden="true" className="size-5" />}
        title="You do not have access to this screen"
        description="Managing configuration here needs the config.manage permission in this country. Ask your administrator if you believe you should have it."
      />
    </div>
  );
}

/**
 * One sentence for an action that failed.
 *
 * The API's envelope message is already a human sentence (spec §10) and is
 * preferred verbatim — it names the actual refusal ("Agent key 12 was already
 * revoked at …") where a client-invented sentence could only be vague. The
 * fallback covers non-envelope failures (proxy pages, transport).
 */
export function errorSentence(error: unknown, fallback: string): string {
  if (error instanceof ApiRequestError && error.message !== '') return error.message;
  if (error instanceof ApiTransportError) return error.message;
  return fallback;
}
